import React, { useState } from "react";
import { useData } from "../../context/DataContext";
import Card from "../ui/Card";
import Table from "../ui/Table";
import Button from "../ui/Button";
import Modal from "../ui/Modal";

const AssignPlaybookModal = ({ isOpen, onClose, selectedPlayer }) => {
  const { playbooks, assignPlaybookToPlayers } = useData();
  const [selectedPlaybooks, setSelectedPlaybooks] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);

  const handlePlaybookToggle = (playbookId) => {
    setSelectedPlaybooks((prev) =>
      prev.includes(playbookId)
        ? prev.filter((id) => id !== playbookId)
        : [...prev, playbookId]
    );
  };

  const handleAssign = async () => {
    if (selectedPlaybooks.length > 0 && selectedPlayer && !isAssigning) {
      setIsAssigning(true);
      try {
        await Promise.all(
          selectedPlaybooks.map((playbookId) =>
            assignPlaybookToPlayers(playbookId, [selectedPlayer.id])
          )
        );
        setSelectedPlaybooks([]);
        onClose();
      } catch (error) {
      } finally {
        setIsAssigning(false);
      }
    }
  };

  const handleClose = () => {
    if (!isAssigning) {
      setSelectedPlaybooks([]);
      onClose();
    }
  };

  if (!selectedPlayer) return null;

  // Filter out playbooks already assigned to this player
  const availablePlaybooks = playbooks.filter(
    (playbook) => !selectedPlayer.assignedPlaybooks.includes(playbook.id)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Assign Playbooks to ${selectedPlayer.name}`}
    >
      <div>
        <p className="helper-text">
          Select playbooks to assign to <strong>{selectedPlayer.name}</strong>:
        </p>

        {availablePlaybooks.length === 0 ? (
          <p className="helper-text-italic">
            All playbooks are already assigned to this player.
          </p>
        ) : (
          <div className="modal-content-section">
            {availablePlaybooks.map((playbook) => (
              <label
                key={playbook.id}
                className={`playbook-option ${selectedPlaybooks.includes(playbook.id) ? "selected" : ""
                  }`}
              >
                <input
                  type="checkbox"
                  checked={selectedPlaybooks.includes(playbook.id)}
                  onChange={() => handlePlaybookToggle(playbook.id)}
                  className="playbook-option-checkbox"
                />
                <div>
                  <div className="playbook-option-name">
                    {playbook.title || playbook.name}
                  </div>
                  <div className="playbook-option-meta">
                    Uploaded:{" "}
                    {new Date(playbook.uploadDate).toLocaleDateString()}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="modal-footer-custom">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isAssigning}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={selectedPlaybooks.length === 0 || isAssigning}
          >
            {isAssigning ? (
              <>
                <div className="spinner spinner-small"></div>
                Assigning...
              </>
            ) : (
              <>
                Assign{" "}
                {selectedPlaybooks.length > 0
                  ? `${selectedPlaybooks.length} Playbook${selectedPlaybooks.length !== 1 ? "s" : ""
                  }`
                  : "Playbooks"}
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const InvitePlayerModal = ({ isOpen, onClose }) => {
  const { invitePlayer } = useData();
  const [email, setEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /\S+@\S+\.\S+/;
    return emailRegex.test(email);
  };

  const handleInvite = async () => {
    // Reset states
    setError("");
    setSuccess(false);

    // Validation
    if (!email.trim()) {
      setError("Email address is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsInviting(true);

    try {
      await invitePlayer(email);
      setSuccess(true);
      setEmail("");

      // Close modal after a short delay to show success message
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.message || "Failed to invite player. Please try again.");
    } finally {
      setIsInviting(false);
    }
  };

  const handleClose = () => {
    if (!isInviting) {
      setEmail("");
      setError("");
      setSuccess(false);
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isInviting) {
      handleInvite();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Invite Player">
      <div>
        <p className="helper-text">
          Enter the email address of the player you want to invite to your team.
        </p>

        <div className="modal-content-section">
          <div className="form-group">
            <label htmlFor="playerEmail" className="form-label">
              Player Email
            </label>
            <input
              type="email"
              id="playerEmail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`form-input ${error ? "form-input-error" : ""}`}
              placeholder="player@example.com"
              disabled={isInviting || success}
              autoFocus
            />
            {error && <p className="form-error">{error}</p>}
            {success && (
              <p className="form-success">
                Player invited successfully! They will now appear in your player list.
              </p>
            )}
          </div>
        </div>

        <div className="modal-footer-custom">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isInviting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleInvite}
            disabled={isInviting || success}
          >
            {isInviting ? (
              <>
                <div className="spinner spinner-small"></div>
                Inviting...
              </>
            ) : success ? (
              "Invited!"
            ) : (
              "Invite Player"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const RemovePlayerModal = ({ isOpen, onClose, selectedPlayer }) => {
  const { removePlayer } = useData();
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState("");

  const handleRemove = async () => {
    if (!selectedPlayer) return;

    setIsRemoving(true);
    setError("");

    try {
      await removePlayer(selectedPlayer.id);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to remove player. Please try again.");
    } finally {
      setIsRemoving(false);
    }
  };

  const handleClose = () => {
    if (!isRemoving) {
      setError("");
      onClose();
    }
  };

  if (!selectedPlayer) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Remove Player">
      <div>
        <p className="helper-text">
          Are you sure you want to remove <strong>{selectedPlayer.name}</strong> from your team?
        </p>
        <p className="helper-text" style={{ marginTop: "0.5rem", color: "#dc2626" }}>
          This will clear their team assignment and they will no longer appear in your player list.
        </p>

        {error && (
          <div className="form-submit-error" style={{ marginTop: "1rem" }}>
            <p className="form-submit-error-text">{error}</p>
          </div>
        )}

        <div className="modal-footer-custom" style={{ marginTop: "1.5rem" }}>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isRemoving}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRemove}
            disabled={isRemoving}
          >
            {isRemoving ? (
              <>
                <div className="spinner spinner-small"></div>
                Removing...
              </>
            ) : (
              "Remove Player"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const PlayerList = () => {
  const { players, loadingPlayers, playbooks } = useData();
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [playerToRemove, setPlayerToRemove] = useState(null);

  const handleAssignClick = (player) => {
    setSelectedPlayer(player);
    setIsAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedPlayer(null);
  };

  const handleInviteClick = () => {
    setIsInviteModalOpen(true);
  };

  const closeInviteModal = () => {
    setIsInviteModalOpen(false);
  };

  const handleRemoveClick = (player) => {
    setPlayerToRemove(player);
    setIsRemoveModalOpen(true);
  };

  const closeRemoveModal = () => {
    setIsRemoveModalOpen(false);
    setPlayerToRemove(null);
  };

  const columns = [
    {
      header: "Player Name",
      accessor: "name",
      width: "25%",
    },
    {
      header: "Email",
      accessor: "email",
      width: "30%",
    },
    {
      header: "Assigned Playbooks",
      accessor: "assignedPlaybooks",
      width: "20%",
      render: (playersList) => (
        <span
          className={`player-count-badge ${playersList?.assignedPlaybooks?.length > 0
            ? "has-playbooks"
            : "no-playbooks"
            }`}
        >
          {playersList?.assignedPlaybooks?.length}
        </span>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      width: "25%",
      render: (player) => (
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "center" }}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleAssignClick(player)}
            disabled={!playbooks || playbooks.length === 0}
            style={{ whiteSpace: "nowrap" }}
          >
            Assign Playbook
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleRemoveClick(player)}
            style={{ whiteSpace: "nowrap" }}
          >
            Remove
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Card
        title="Players"
        headerActions={
          <Button
            variant="primary"
            onClick={handleInviteClick}
          >
            + Invite Player
          </Button>
        }
      >
        {loadingPlayers ? (
          <div className="loading-container">
            <div className="spinner" />
            <div className="loading-text">Loading players...</div>
          </div>
        ) : players && players.length > 0 ? (
          <Table columns={columns} data={players} />
        ) : (
          <div className="empty-state">No players available.</div>
        )}
      </Card>

      <AssignPlaybookModal
        isOpen={isAssignModalOpen}
        onClose={closeAssignModal}
        selectedPlayer={selectedPlayer}
      />

      <InvitePlayerModal
        isOpen={isInviteModalOpen}
        onClose={closeInviteModal}
      />

      <RemovePlayerModal
        isOpen={isRemoveModalOpen}
        onClose={closeRemoveModal}
        selectedPlayer={playerToRemove}
      />
    </>
  );
};

export default PlayerList;
